import React, { useState, useEffect } from 'react';

const CleanNestWebsite = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', service: 'turnover', propertySize: '1bed', message: ''
  });
  const [formStatus, setFormStatus] = useState('idle');
  const [calculator, setCalculator] = useState({
    service: 'turnover',
    size: '1bed',
    addOns: { messy: false, linen: false, sameDay: false, carpet: false, windows: false }
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      setShowWhatsApp(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const pricing = {
    turnover: { studio: [55,65], '1bed': [60,75], '2bed': [75,95], '3bed': [95,125], '4bed': [125,150] },
    deepClean: { studio: [150,180], '1bed': [180,220], '2bed': [220,280], '3bed': [280,360], '4bed': [360,480] },
    launch: { studio: [500,650], '1bed': [500,650], '2bed': [650,800], '3bed': [800,950], '4bed': [950,1500] }
  };

  const addOnPrices = { messy: 35, linen: 20, sameDay: 25, carpet: 60, windows: 45 };

  const calculatePrice = () => {
    const [min, max] = pricing[calculator.service][calculator.size];
    let addOnTotal = Object.keys(calculator.addOns).reduce((sum, key) => 
      sum + (calculator.addOns[key] ? addOnPrices[key] : 0), 0);
    return { min: min + addOnTotal, max: max + addOnTotal, avg: Math.round((min + max) / 2 + addOnTotal) };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus('sending');
    setTimeout(() => {
      setFormStatus('success');
      setFormData({ name: '', email: '', phone: '', service: 'turnover', propertySize: '1bed', message: '' });
      setTimeout(() => setFormStatus('idle'), 5000);
    }, 1500);
  };

  const price = calculatePrice();

  const images = [
    { url: 'https://images.unsplash.com/photo-1600331574095-4a20d3d8dd77?w=800', label: 'Before', type: 'before' },
    { url: 'https://images.unsplash.com/photo-1595940929854-47f1bca6f845?w=800', label: 'After', type: 'after' },
    { url: 'https://images.unsplash.com/photo-1657213077302-79e89564a042?w=800', label: 'Before', type: 'before' },
    { url: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=800', label: 'After', type: 'after' },
    { url: 'https://images.unsplash.com/photo-1743527705972-4c8506afe856?w=800', label: 'Before', type: 'before' },
    { url: 'https://images.unsplash.com/photo-1546617956-e3deefb51d58?w=800', label: 'After', type: 'after' },
    { url: 'https://images.pexels.com/photos/4098838/pexels-photo-4098838.jpeg?w=800', label: 'Cleaning', type: 'process' },
    { url: 'https://images.pexels.com/photos/4239141/pexels-photo-4239141.jpeg?w=800', label: 'Cleaning', type: 'process' },
    { url: 'https://images.unsplash.com/photo-1496180727794-817822f65950?w=800', label: 'After', type: 'after' },
    { url: 'https://images.pexels.com/photos/32471850/pexels-photo-32471850.jpeg?w=800', label: 'After', type: 'after' },
    { url: 'https://images.pexels.com/photos/245208/pexels-photo-245208.jpeg?w=800', label: 'After', type: 'after' },
    { url: 'https://images.unsplash.com/photo-1759249919714-5cbf77de4d9d?w=800', label: 'Transformation', type: 'before-after' }
  ];

  const faqs = [
    { q: 'What areas do you cover in Cardiff?', a: 'We serve Cardiff city centre, Cardiff Bay, Roath, Cathays, Whitchurch, Radyr, Pontcanna, Grangetown, and surrounding areas.' },
    { q: 'How do I book a cleaning service?', a: 'Book via phone, WhatsApp, or our contact form. We respond within 2 hours during business hours.' },
    { q: 'Do you provide photo reports?', a: 'Yes! We provide timestamped photo reports after every clean for complete transparency.' },
    { q: 'Are your staff DBS checked?', a: 'Absolutely. All team members are DBS-checked and fully insured.' },
    { q: 'Can you handle same-day emergencies?', a: 'Yes, same-day emergency service available (additional £25 fee applies).' },
    { q: 'What makes CleanNest different?', a: 'We\'re local Cardiff experts using eco-friendly products with fast response times and photo proof.' },
    { q: 'Do you offer property management?', a: 'Yes! Full short-stay let management at 15% commission including guest communication, dynamic pricing, and review management.' },
    { q: 'What payment methods do you accept?', a: 'Bank transfers, credit/debit cards, and monthly invoicing for regular clients.' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 sm:gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_improve-ai-site/artifacts/ft8chqsd_AZ0cDSTaxSUNYSuP8l6R3g-AZ0cDSTaaI54DLBl-QXdvA.jpg"
              alt="CleanNest Cardiff"
              className="h-10 w-10 sm:h-12 sm:w-12 object-contain transition-transform hover:scale-105"
            />
            <div className="text-lg sm:text-xl font-serif text-green-700">
              CleanNest <span className="text-xs sm:text-sm italic text-gray-600">Cardiff</span>
            </div>
          </div>
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
            className="md:hidden text-2xl p-2 hover:bg-gray-100 rounded transition-colors"
            aria-label="Toggle menu">
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
          <div className="hidden md:flex gap-6 lg:gap-8 items-center">
            {['Services', 'Platform', 'Gallery', 'Pricing', 'FAQ', 'Contact'].map(item => (
              <a key={item} href={`#${item.toLowerCase()}`} 
                 className="text-sm lg:text-base hover:text-green-700 transition-colors duration-200 font-medium">
                {item}
              </a>
            ))}
            <a href="#contact" 
               className="bg-green-700 text-white px-5 lg:px-6 py-2 rounded-lg hover:bg-green-800 transition-all duration-200 shadow-md hover:shadow-lg text-sm lg:text-base font-medium">
              Get Quote
            </a>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t shadow-lg animate-slide-down">
            {['Services', 'Platform', 'Gallery', 'Pricing', 'FAQ', 'Contact'].map(item => (
              <a key={item} href={`#${item.toLowerCase()}`} onClick={() => setMobileMenuOpen(false)} 
                 className="block px-6 py-4 hover:bg-gray-50 transition-colors border-b border-gray-100 font-medium">
                {item}
              </a>
            ))}
            <a href="#contact" onClick={() => setMobileMenuOpen(false)}
               className="block mx-6 my-4 text-center bg-green-700 text-white py-3 rounded-lg hover:bg-green-800 transition-colors font-medium">
              Get Quote
            </a>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="pt-24 sm:pt-32 lg:pt-40 pb-16 sm:pb-20 lg:pb-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-green-50 via-white to-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-72 h-72 bg-green-700 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-green-500 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-8 lg:gap-16 items-center relative z-10">
          <div className="text-center lg:text-left">
            <div className="inline-block bg-green-100 text-green-700 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium mb-6 sm:mb-8 shadow-sm">
              ✨ Cardiff's Premier Cleaning Service
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-serif mb-4 sm:mb-6 leading-tight">
              A <em className="italic text-green-700">Clean Nest</em> — Every Time
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto lg:mx-0">
              Professional commercial cleaning and short-stay let management powered by advanced technology, across Cardiff.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <a href="#contact" 
                 className="bg-green-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:bg-green-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-medium text-center">
                Get Free Quote
              </a>
              <a href="#services" 
                 className="border-2 border-gray-300 px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:border-green-700 hover:bg-green-50 transition-all duration-200 font-medium text-center">
                View Services
              </a>
            </div>
            <div className="flex flex-wrap gap-6 sm:gap-8 justify-center lg:justify-start mt-8 sm:mt-12">
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-green-700">30% Off</div>
                <div className="text-xs sm:text-sm text-gray-600">Launch Discount</div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-green-700">£0</div>
                <div className="text-xs sm:text-sm text-gray-600">Hidden Fees</div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-green-700">📸</div>
                <div className="text-xs sm:text-sm text-gray-600">Photo Reports</div>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-700 to-green-800 p-8 sm:p-12 lg:p-16 rounded-2xl text-white text-center shadow-2xl transform hover:scale-105 transition-transform duration-300">
            <img 
              src="https://customer-assets.emergentagent.com/job_improve-ai-site/artifacts/ft8chqsd_AZ0cDSTaxSUNYSuP8l6R3g-AZ0cDSTaaI54DLBl-QXdvA.jpg"
              alt="CleanNest Cardiff Logo"
              className="w-32 h-32 sm:w-48 sm:h-48 lg:w-56 lg:h-56 mx-auto object-contain bg-white rounded-2xl p-4 sm:p-6 shadow-lg"
            />
            <div className="mt-6 sm:mt-8 text-lg sm:text-xl font-medium">Your Trusted Cleaning Partner</div>
            <div className="mt-4 text-sm sm:text-base text-green-100">Fully Insured • DBS Checked • Eco-Friendly</div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif mb-4">
              Our <em className="italic text-green-700">Services</em>
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Commercial cleaning and short-stay let management solutions for Cardiff businesses and property owners
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="bg-gradient-to-br from-gray-50 to-white p-6 sm:p-8 lg:p-10 rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="text-4xl mb-4">🏢</div>
              <h3 className="text-xl sm:text-2xl font-serif mb-4 sm:mb-6 text-gray-900">Commercial Cleaning</h3>
              <ul className="space-y-3 sm:space-y-4">
                {['Office Cleaning', 'Regular House Cleans', 'End-of-Tenancy Deep Clean', 'Carpet Steam Cleaning', 'Window Cleaning'].map(s => (
                  <li key={s} className="flex items-start gap-3 text-sm sm:text-base">
                    <span className="text-green-700 text-xl flex-shrink-0">•</span>
                    <span className="text-gray-700">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-white p-6 sm:p-8 lg:p-10 rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="text-4xl mb-4">🏡</div>
              <h3 className="text-xl sm:text-2xl font-serif mb-4 sm:mb-6 text-gray-900">Short-Stay Properties</h3>
              <ul className="space-y-3 sm:space-y-4">
                {['Professional Turnover Cleaning', 'Linen & Laundry Service', 'Restocking Consumables', 'Oven & Appliance Clean', 'Photo Reports'].map(s => (
                  <li key={s} className="flex items-start gap-3 text-sm sm:text-base">
                    <span className="text-green-700 text-xl flex-shrink-0">•</span>
                    <span className="text-gray-700">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-white p-6 sm:p-8 lg:p-10 rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-xl sm:text-2xl font-serif mb-4 sm:mb-6 text-gray-900">Let Management</h3>
              <ul className="space-y-3 sm:space-y-4">
                {['Guest Communication 24/7', 'Dynamic Pricing Strategy', 'Calendar Management', 'Check-in & Check-out', 'Review Management'].map(s => (
                  <li key={s} className="flex items-start gap-3 text-sm sm:text-base">
                    <span className="text-green-700 text-xl flex-shrink-0">•</span>
                    <span className="text-gray-700">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Technology Section */}
      <section id="platform" className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-green-50 via-white to-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-96 h-96 bg-green-600 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-block bg-green-100 text-green-700 px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6">
              🚀 Technology-Powered Service
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif mb-4 sm:mb-6">
              The <em className="italic text-green-700">CleanNest Platform</em>
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
              We've built a comprehensive digital platform to manage every aspect of your property — so you don't have to.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-12">
            <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-green-100 rounded-lg flex items-center justify-center mb-4 sm:mb-6">
                <span className="text-2xl sm:text-3xl">📊</span>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-3 text-gray-900">Smart Dashboard</h3>
              <p className="text-sm sm:text-base text-gray-600">
                Real-time KPIs, today's schedule, and AI-powered insights at your fingertips.
              </p>
            </div>
            
            <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-green-100 rounded-lg flex items-center justify-center mb-4 sm:mb-6">
                <span className="text-2xl sm:text-3xl">🤖</span>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-3 text-gray-900">AI Automation</h3>
              <p className="text-sm sm:text-base text-gray-600">
                Auto-scheduling, pricing intelligence, and automated guest messaging.
              </p>
            </div>
            
            <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-green-100 rounded-lg flex items-center justify-center mb-4 sm:mb-6">
                <span className="text-2xl sm:text-3xl">👥</span>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-3 text-gray-900">Client Portal</h3>
              <p className="text-sm sm:text-base text-gray-600">
                Property owners get 24/7 access to job history, photos, and reports.
              </p>
            </div>
            
            <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-green-100 rounded-lg flex items-center justify-center mb-4 sm:mb-6">
                <span className="text-2xl sm:text-3xl">📅</span>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-3 text-gray-900">Calendar Management</h3>
              <p className="text-sm sm:text-base text-gray-600">
                Color-coded job calendar with detailed daily scheduling and team coordination.
              </p>
            </div>
            
            <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-green-100 rounded-lg flex items-center justify-center mb-4 sm:mb-6">
                <span className="text-2xl sm:text-3xl">📸</span>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-3 text-gray-900">Photo Reports</h3>
              <p className="text-sm sm:text-base text-gray-600">
                Timestamped photo documentation after every clean for complete transparency.
              </p>
            </div>
            
            <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-green-100 rounded-lg flex items-center justify-center mb-4 sm:mb-6">
                <span className="text-2xl sm:text-3xl">🔔</span>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-3 text-gray-900">Auto-Notifications</h3>
              <p className="text-sm sm:text-base text-gray-600">
                Real-time alerts for job assignments, completions, and important updates.
              </p>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-green-700 to-green-800 p-8 sm:p-10 lg:p-12 rounded-2xl text-white text-center shadow-2xl">
            <h3 className="text-2xl sm:text-3xl font-serif mb-4">Why This Matters</h3>
            <p className="text-base sm:text-lg text-green-50 max-w-3xl mx-auto mb-6 sm:mb-8">
              Our platform transforms cleaning from a manual task into a streamlined digital experience. You get complete visibility, 
              automated workflows, and data-driven insights — helping you save time, reduce costs, and scale your property business effortlessly.
            </p>
            <div className="grid sm:grid-cols-3 gap-6 sm:gap-8 max-w-3xl mx-auto">
              <div>
                <div className="text-3xl sm:text-4xl font-bold mb-2">50%</div>
                <div className="text-sm sm:text-base text-green-100">Time Saved</div>
              </div>
              <div>
                <div className="text-3xl sm:text-4xl font-bold mb-2">100%</div>
                <div className="text-sm sm:text-base text-green-100">Transparent</div>
              </div>
              <div>
                <div className="text-3xl sm:text-4xl font-bold mb-2">24/7</div>
                <div className="text-sm sm:text-base text-green-100">Access</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section id="gallery" className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif mb-4">
              See the <em className="italic text-green-700">Difference</em>
            </h2>
            <p className="text-base sm:text-lg text-gray-600">
              Before & after transformations that speak for themselves
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
            {images.map((img, i) => (
              <div key={i} onClick={() => setSelectedImage(img.url)} 
                   className="relative aspect-square bg-gray-200 rounded-xl overflow-hidden cursor-pointer group">
                <img src={img.url} alt={img.label} 
                     className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
                    <span className={`inline-block px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-full font-medium ${
                      img.type === 'before' ? 'bg-red-500 text-white' : 
                      img.type === 'after' ? 'bg-green-500 text-white' : 
                      'bg-blue-500 text-white'
                    }`}>
                      {img.label}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {selectedImage && (
          <div onClick={() => setSelectedImage(null)} 
               className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 sm:p-6 backdrop-blur-sm animate-fade-in">
            <img src={selectedImage} alt="Preview" 
                 className="max-w-full max-h-full rounded-lg shadow-2xl" 
                 onClick={(e) => e.stopPropagation()} />
            <button onClick={() => setSelectedImage(null)} 
                    className="absolute top-4 right-4 text-white text-4xl sm:text-5xl hover:text-green-400 transition-colors">
              ×
            </button>
          </div>
        )}
      </section>

      {/* Pricing Calculator */}
      <section id="pricing" className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif mb-4">
              Transparent <em className="italic text-green-700">Pricing</em>
            </h2>
            <p className="text-base sm:text-lg text-gray-600">
              Calculate your estimate in seconds — no hidden fees
            </p>
          </div>
          <div className="bg-white p-6 sm:p-8 lg:p-10 rounded-2xl shadow-xl border border-gray-100">
            <div className="mb-6 sm:mb-8">
              <label className="block font-semibold mb-3 sm:mb-4 text-base sm:text-lg text-gray-900">Service Type</label>
              <div className="grid sm:grid-cols-3 gap-3 sm:gap-4">
                {[['turnover', 'Turnover'], ['deepClean', 'Deep Clean'], ['launch', 'Launch Service']].map(([val, label]) => (
                  <button key={val} onClick={() => setCalculator({...calculator, service: val})}
                    className={`p-3 sm:p-4 rounded-xl border-2 font-medium transition-all duration-200 text-sm sm:text-base ${
                      calculator.service === val 
                        ? 'border-green-700 bg-green-50 text-green-700 shadow-md' 
                        : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                    }`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mb-6 sm:mb-8">
              <label className="block font-semibold mb-3 sm:mb-4 text-base sm:text-lg text-gray-900">Property Size</label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-3">
                {['studio', '1bed', '2bed', '3bed', '4bed'].map(size => (
                  <button key={size} onClick={() => setCalculator({...calculator, size})}
                    className={`p-2 sm:p-3 rounded-xl border-2 font-medium transition-all duration-200 text-xs sm:text-sm ${
                      calculator.size === size 
                        ? 'border-green-700 bg-green-50 text-green-700 shadow-md' 
                        : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                    }`}>
                    {size === 'studio' ? 'Studio' : size.replace('bed', '-bed')}
                  </button>
                ))}
              </div>
            </div>
            <div className="mb-6 sm:mb-8">
              <label className="block font-semibold mb-3 sm:mb-4 text-base sm:text-lg text-gray-900">Add-ons (Optional)</label>
              <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                {[['messy', 'Extra-messy'], ['linen', 'Linen hire'], ['sameDay', 'Same-day'], ['carpet', 'Carpet clean'], ['windows', 'Window clean']].map(([key, label]) => (
                  <label key={key} 
                         className="flex items-center gap-3 p-3 sm:p-4 border-2 rounded-xl cursor-pointer hover:bg-gray-50 transition-all duration-200 border-gray-200 hover:border-gray-300">
                    <input type="checkbox" checked={calculator.addOns[key]} 
                      onChange={(e) => setCalculator({...calculator, addOns: {...calculator.addOns, [key]: e.target.checked}})}
                      className="w-4 h-4 sm:w-5 sm:h-5 text-green-700 rounded focus:ring-green-500" />
                    <span className="flex-1 text-sm sm:text-base font-medium">{label}</span>
                    <span className="text-gray-600 font-semibold text-sm sm:text-base">+£{addOnPrices[key]}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-600 rounded-2xl p-6 sm:p-8 lg:p-10 shadow-lg">
              <div className="text-center">
                <div className="text-sm sm:text-base text-gray-700 mb-2 font-medium">Estimated Price</div>
                <div className="text-4xl sm:text-5xl lg:text-6xl font-serif text-green-700 mb-3 sm:mb-4">
                  £{price.min} - £{price.max}
                </div>
                <div className="text-sm sm:text-base text-gray-700 mb-6">
                  Average: <strong className="text-lg sm:text-xl text-green-700">£{price.avg}</strong>
                </div>
                <a href="#contact" 
                   className="inline-block bg-green-700 text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-xl hover:bg-green-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-medium text-sm sm:text-base">
                  Get Exact Quote →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif mb-4">
              Common <em className="italic text-green-700">Questions</em>
            </h2>
            <p className="text-base sm:text-lg text-gray-600">
              Everything you need to know about our services
            </p>
          </div>
          <div className="space-y-3 sm:space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-200">
                <button onClick={() => setActiveAccordion(activeAccordion === i ? null : i)}
                  className="w-full p-4 sm:p-5 lg:p-6 text-left flex justify-between items-center hover:bg-gray-50 transition-colors">
                  <span className="font-semibold text-sm sm:text-base lg:text-lg text-gray-900 pr-4">{faq.q}</span>
                  <span className={`text-green-700 text-xl sm:text-2xl transition-transform duration-300 flex-shrink-0 ${
                    activeAccordion === i ? 'rotate-180' : ''
                  }`}>▼</span>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${
                  activeAccordion === i ? 'max-h-96' : 'max-h-0'
                }`}>
                  <div className="px-4 sm:px-5 lg:px-6 pb-4 sm:pb-5 lg:pb-6 text-gray-600 text-sm sm:text-base leading-relaxed">
                    {faq.a}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif mb-4">
              Get in <em className="italic text-green-700">Touch</em>
            </h2>
            <p className="text-base sm:text-lg text-gray-600">
              We respond within 2 hours during business hours
            </p>
          </div>
          <form onSubmit={handleSubmit} className="bg-white p-6 sm:p-8 lg:p-10 rounded-2xl shadow-xl border border-gray-100">
            <div className="space-y-4 sm:space-y-5">
              <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
                <input type="text" placeholder="Your Name *" value={formData.name} 
                  onChange={(e) => setFormData({...formData, name: e.target.value})} required
                  className="p-3 sm:p-4 border-2 border-gray-200 rounded-xl focus:border-green-700 focus:outline-none transition-colors text-sm sm:text-base" />
                <input type="email" placeholder="Email *" value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})} required
                  className="p-3 sm:p-4 border-2 border-gray-200 rounded-xl focus:border-green-700 focus:outline-none transition-colors text-sm sm:text-base" />
              </div>
              <input type="tel" placeholder="Phone *" value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})} required
                className="w-full p-3 sm:p-4 border-2 border-gray-200 rounded-xl focus:border-green-700 focus:outline-none transition-colors text-sm sm:text-base" />
              <textarea placeholder="Tell us about your needs *" rows="5" value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})} required
                className="w-full p-3 sm:p-4 border-2 border-gray-200 rounded-xl focus:border-green-700 focus:outline-none transition-colors resize-none text-sm sm:text-base"></textarea>
              <button type="submit" disabled={formStatus === 'sending'}
                className="w-full bg-green-700 text-white py-3 sm:py-4 rounded-xl hover:bg-green-800 disabled:opacity-50 transition-all duration-200 shadow-lg hover:shadow-xl font-medium text-sm sm:text-base">
                {formStatus === 'sending' ? 'Sending...' : formStatus === 'success' ? '✓ Message Sent!' : 'Get Free Quote'}
              </button>
              {formStatus === 'success' && (
                <div className="bg-green-50 border-2 border-green-200 text-green-800 p-4 sm:p-5 rounded-xl text-sm sm:text-base">
                  Thanks! We'll respond within 2 hours during business hours.
                </div>
              )}
            </div>
            
            <div className="text-center mt-8 sm:mt-10">
              <p className="text-gray-600 mb-5 sm:mb-6 text-sm sm:text-base font-medium">Or connect with us on social media:</p>
              <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
                <a href="https://wa.me/447700000000" target="_blank" rel="noopener noreferrer"
                   className="flex items-center justify-center gap-2 sm:gap-3 bg-green-500 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl hover:bg-green-600 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 font-medium text-sm sm:text-base">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  WhatsApp
                </a>
                <a href="https://facebook.com/cleannestcardiff" target="_blank" rel="noopener noreferrer"
                   className="flex items-center justify-center gap-2 sm:gap-3 bg-blue-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl hover:bg-blue-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 font-medium text-sm sm:text-base">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Facebook
                </a>
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 lg:gap-12">
          <div>
            <div className="flex items-center gap-2 mb-4 sm:mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_improve-ai-site/artifacts/ft8chqsd_AZ0cDSTaxSUNYSuP8l6R3g-AZ0cDSTaaI54DLBl-QXdvA.jpg"
                alt="CleanNest Cardiff"
                className="h-10 w-10 sm:h-12 sm:w-12 object-contain bg-white rounded-lg p-1.5"
              />
              <div className="text-lg sm:text-xl font-serif">CleanNest Cardiff</div>
            </div>
            <p className="text-gray-400 mb-4 sm:mb-6 text-sm sm:text-base">Professional cleaning across Cardiff</p>
            <div className="flex gap-3">
              <a href="https://wa.me/447700000000" target="_blank" rel="noopener noreferrer" 
                 className="bg-green-500 p-2.5 sm:p-3 rounded-lg hover:bg-green-600 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
              </a>
              <a href="https://facebook.com/cleannestcardiff" target="_blank" rel="noopener noreferrer"
                 className="bg-blue-600 p-2.5 sm:p-3 rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-3 sm:mb-4 text-base sm:text-lg">Services</h4>
            <div className="space-y-2 text-gray-400 text-sm sm:text-base">
              <div className="hover:text-white transition-colors cursor-pointer">Commercial Cleaning</div>
              <div className="hover:text-white transition-colors cursor-pointer">Office Cleaning</div>
              <div className="hover:text-white transition-colors cursor-pointer">Short-Stay Management</div>
              <div className="hover:text-white transition-colors cursor-pointer">Platform Access</div>
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-3 sm:mb-4 text-base sm:text-lg">Contact</h4>
            <div className="space-y-2 text-gray-400 text-sm sm:text-base">
              <div className="hover:text-white transition-colors">029 2012 3456</div>
              <div className="hover:text-white transition-colors">hello@cleannestcardiff.co.uk</div>
              <div className="hover:text-white transition-colors">Cardiff, Wales</div>
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-3 sm:mb-4 text-base sm:text-lg">Hours</h4>
            <div className="space-y-2 text-gray-400 text-sm sm:text-base">
              <div>Mon-Fri: 9am-5pm</div>
              <div>Sat: 9am-1pm</div>
              <div>Sun: Closed</div>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-10 sm:mt-12 pt-8 sm:pt-10 border-t border-gray-800 text-center text-gray-400 text-xs sm:text-sm">
          © 2025 CleanNest Cardiff. All rights reserved.
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      {showWhatsApp && (
        <a href="https://wa.me/447700000000?text=Hi%20CleanNest%20Cardiff!%20I'd%20like%20to%20enquire%20about%20your%20services."
          target="_blank" rel="noopener noreferrer"
          className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 bg-green-500 text-white p-3.5 sm:p-4 rounded-full shadow-2xl hover:bg-green-600 z-40 transition-all duration-300 hover:scale-110 animate-bounce-subtle">
          <span className="text-2xl sm:text-3xl">💬</span>
        </a>
      )}
    </div>
  );
};

export default CleanNestWebsite;
